import styled from "@emotion/styled";

import {
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSProgressBar,
  MDSProgressBarColor,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import type { MemoryManifest, MemoryRun } from "@/pages/agent/memory/memory-api";

const PHASES: Array<{
  phase: MemoryRun["phase"];
  label: (manifest: MemoryManifest) => string;
  description: string;
}> = [
  {
    phase: "discovering",
    label: () => "Reading your notes",
    description: "Finding relevant context",
  },
  {
    phase: "mapping",
    label: () => "Finding what matters",
    description: "Extracting items, details, and connections",
  },
  {
    phase: "reducing",
    label: () => "Connecting related knowledge",
    description: "Combining duplicates and their supporting notes",
  },
  {
    phase: "publishing",
    label: manifest => `Publishing ${manifest.display_name}`,
    description: "Preparing the views you can browse and correct",
  },
];

const phaseIndex = (phase: MemoryRun["phase"]): number => {
  if (phase === "compiling") return -1;
  if (phase === "complete") return PHASES.length;
  return PHASES.findIndex(item => item.phase === phase);
};

const progressPercent = (run: MemoryRun): number => {
  switch (run.phase) {
    case "compiling":
      return 6;
    case "discovering":
      return 14;
    case "mapping":
      return run.progress_total
        ? 18 + Math.round((run.progress_current / run.progress_total) * 58)
        : 36;
    case "reducing":
      return 82;
    case "publishing":
      return 94;
    case "complete":
      return 100;
  }
};

export const MemoryLoadingState = ({
  run,
  manifest,
}: {
  run: MemoryRun;
  manifest: MemoryManifest;
}) => {
  const activeIndex = phaseIndex(run.phase);
  const currentPhase = PHASES[Math.max(0, Math.min(activeIndex, PHASES.length - 1))];
  const isCustomerIntelligence = manifest.key === "customer-intelligence";

  return (
    <LoadingRoot aria-live="polite">
      <Orb aria-hidden="true">
        <MDSIcon kind={MDSIconKind.Cells} size={26} color={MDSIconColor.Primary} />
      </Orb>
      <Header>
        <MDSText variant={MDSTextVariant.HeaderXl}>
          {isCustomerIntelligence
            ? "Building your customer memory"
            : `Building ${manifest.display_name}`}
        </MDSText>
        <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.ParagraphSm}>
          Mem Agent is turning the knowledge already in your notes into one connected, living view.
        </MDSText>
      </Header>

      <ProgressBlock>
        <ProgressLabel>
          <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.SemiBold}>
            {currentPhase?.label(manifest) ?? "Preparing your memory"}
          </MDSText>
          <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
            {run.progress_total
              ? `${run.progress_current.toLocaleString()} of ${run.progress_total.toLocaleString()} notes`
              : `${progressPercent(run)}%`}
          </MDSText>
        </ProgressLabel>
        <MDSProgressBar
          aria-label={`${manifest.display_name} backfill progress`}
          value={progressPercent(run)}
          minValue={0}
          maxValue={100}
          color={MDSProgressBarColor.Emphasis}
          size="large"
          rounded
        />
      </ProgressBlock>

      <PhaseList>
        {PHASES.map((item, index) => {
          const isComplete = index < activeIndex || run.phase === "complete";
          const isActive = index === activeIndex;
          return (
            <PhaseRow key={item.phase} $isActive={isActive}>
              <PhaseIcon $isActive={isActive} $isComplete={isComplete}>
                <MDSIcon
                  kind={
                    isComplete
                      ? MDSIconKind.Check
                      : isActive
                        ? MDSIconKind.SpinnerThird
                        : MDSIconKind.Dot
                  }
                  size={isActive ? 14 : 12}
                />
              </PhaseIcon>
              <PhaseCopy>
                <MDSText
                  color={isActive || isComplete ? MDSTextColor.Primary : MDSTextColor.Tertiary}
                  variant={MDSTextVariant.TextS}
                  weight={isActive ? MDSTextWeight.SemiBold : MDSTextWeight.Regular}
                >
                  {item.label(manifest)}
                </MDSText>
                {isActive ? (
                  <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextXs}>
                    {item.description}
                  </MDSText>
                ) : null}
              </PhaseCopy>
            </PhaseRow>
          );
        })}
      </PhaseList>

      <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
        You can leave this page. Mem Agent will keep working in the background and maintain it as
        your notes change.
      </MDSText>
    </LoadingRoot>
  );
};

const LoadingRoot = styled.section`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
  margin: auto;
  max-width: 620px;
  padding: ${({ theme }) => theme.spacing[10]} 0;
  text-align: center;
  width: 100%;
`;

const Orb = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.button.toggle.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.max};
  box-shadow: 0 12px 40px rgb(0 0 0 / 8%);
  display: flex;
  height: 64px;
  justify-content: center;
  width: 64px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  max-width: 560px;
`;

const ProgressBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  text-align: left;
  width: 100%;
`;

const ProgressLabel = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const PhaseList = styled.div`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing[3]};
  text-align: left;
  width: 100%;
`;

const PhaseRow = styled.div<{ $isActive: boolean }>`
  align-items: flex-start;
  background: ${({ $isActive, theme }) =>
    $isActive ? theme.colors.background.list.hover : "transparent"};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
`;

const PhaseIcon = styled.div<{ $isActive: boolean; $isComplete: boolean }>`
  align-items: center;
  border: 1px solid
    ${({ $isActive, $isComplete, theme }) =>
      $isActive || $isComplete ? theme.colors.border.emphasis : theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.max};
  color: ${({ $isActive, $isComplete, theme }) =>
    $isActive || $isComplete ? theme.colors.text.emphasis : theme.colors.text.tertiary};
  display: flex;
  flex: 0 0 auto;
  height: 24px;
  justify-content: center;
  width: 24px;

  ${({ $isActive }) =>
    $isActive
      ? `
    svg { animation: memory-spin 1.2s linear infinite; }
    @keyframes memory-spin { to { transform: rotate(360deg); } }
  `
      : ""}
`;

const PhaseCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;
